import type { FC, SetStateAction } from "react";
import React from "react";
import type { ValidationError } from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import { isAxiosError } from "axios";
import { UserRoundCog } from "lucide-react";

import { useUpdatePassword } from "@/api/me";
import InsideLabelFieldPassword from "@/components/form/inside-label-field-password";
import GenericModal from "@/components/modals/generic-modal";
import type { UpdatePasswordFormData } from "@/schemas/user";
import { updatePasswordSchema } from "@/schemas/user";
import { resetForm } from "@/utils/form";

interface UpdatePasswordModalProps {
  open: boolean;
  onOpenChange: React.Dispatch<SetStateAction<boolean>>;
}

const UpdatePasswordModal: FC<UpdatePasswordModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { mutateAsync: updatePassword, isPending } = useUpdatePassword();

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      newPasswordConfirmation: "",
    } as UpdatePasswordFormData,
    validators: {
      onSubmit: ({ value }) => {
        const { newPassword, newPasswordConfirmation } = value;
        const returnValue: { fields: { [k: string]: ValidationError } } = {
          fields: {},
        };
        if (newPassword !== newPasswordConfirmation) {
          const passwordMatchError: ValidationError =
            "Le nouveau mot de passe et sa confirmation ne correspondent pas";
          returnValue.fields["newPasswordConfirmation"] = passwordMatchError;
        }
        return returnValue;
      },
    },
    onSubmit: async ({ value, formApi }) => {
      const values = updatePasswordSchema.parse(value);
      await updatePassword(values, {
        onSuccess: () => {
          onOpenChange(false);
          resetForm(values, formApi);
        },
        onError: (e) => {
          if (isAxiosError(e)) {
            if (e.code === "ERR_NETWORK")
              return {
                fields: {
                  comment: "Erreur réseau, merci de réessayer ultérieurement",
                },
              };
          }
        },
      });
    },
  });

  return (
    <GenericModal
      title="Modifier mon mot de passe"
      description="Entrez votre nouveau mot de passe"
      submitLabel="Confirmer"
      logo={<UserRoundCog />}
      open={open}
      setOpen={onOpenChange}
      handleSubmit={handleSubmit}
      isSubmitPending={isPending}
    >
      <div className="flex flex-col px-4 gap-4">
        <InsideLabelFieldPassword
          Field={Field}
          name="currentPassword"
          label="Mot de passe actuel"
          schema={updatePasswordSchema}
          autoComplete="current-password"
        />
        <InsideLabelFieldPassword
          Field={Field}
          name="newPassword"
          label="Nouveau mot de passe"
          schema={updatePasswordSchema}
        />
        <InsideLabelFieldPassword
          Field={Field}
          name="newPasswordConfirmation"
          label="Mot de passe"
          schema={updatePasswordSchema}
        />
      </div>
    </GenericModal>
  );
};

export default UpdatePasswordModal;
